import React, { ReactNode } from 'react';
import './AppHeader.css';

export interface AppHeaderProps {
  /**
   * Header title text or custom element
   */
  title?: string | ReactNode;

  /**
   * Optional subtitle under the title
   */
  subtitle?: ReactNode;

  /**
   * Callback when hamburger menu is clicked
   */
  onMenuClick?: () => void;

  /**
   * Show hamburger menu button
   * @default true
   */
  showMenu?: boolean;

  /**
   * Actions to display on the right side (buttons, credits, etc.)
   */
  actions?: ReactNode;

  /**
   * Slot for left aligned content
   */
  leftSlot?: ReactNode;

  /**
   * Slot for center aligned content
   */
  centerSlot?: ReactNode;

  /**
   * Slot for right aligned content (used when actions undefined)
   */
  rightSlot?: ReactNode;

  /**
   * Optional secondary row content
   */
  secondaryRow?: ReactNode;

  /**
   * Back action button configuration
   */
  backAction?: {
    onClick: () => void;
    label?: string;
    ariaLabel?: string;
  };

  /**
   * Header layout variant
   */
  variant?: 'default' | 'stacked' | 'compact';

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Make header sticky within scroll container
   * @default false
   */
  sticky?: boolean;
}

/**
 * AppHeader - Main application header with hamburger menu
 *
 * Used for main pages (chat, developer hub, etc.) that need
 * a header with hamburger menu to toggle sidebar.
 *
 * @example
 * ```tsx
 * <AppHeader
 *   title="Chats"
 *   onMenuClick={() => setSidebarOpen(true)}
 *   actions={
 *     <span>💰 {credits.toFixed(2)}</span>
 *   }
 * />
 * ```
 */
export const AppHeader: React.FC<AppHeaderProps> = ({
  title,
  subtitle,
  onMenuClick,
  showMenu = true,
  actions,
  leftSlot,
  centerSlot,
  rightSlot,
  secondaryRow,
  backAction,
  variant = 'default',
  className = '',
  sticky = false,
}) => {
  const rightContent = actions !== undefined ? actions : rightSlot;
  const showMenuButton = showMenu && onMenuClick;

  return (
    <header
      className={`app-header app-header--${variant} ${sticky ? 'app-header--sticky' : ''} ${className}`}
    >
      <div className="app-header__row">
        <div className="app-header__left">
          {backAction && (
            <button
              className="app-header-back-btn"
              onClick={backAction.onClick}
              aria-label={backAction.ariaLabel || backAction.label || 'Back'}
              type="button"
            >
              {backAction.label || '←'}
            </button>
          )}
          {showMenuButton && (
            <button
              className="app-header-menu-btn"
              onClick={onMenuClick}
              aria-label="Toggle menu"
              type="button"
            >
              <span className="app-header-menu-line"></span>
              <span className="app-header-menu-line"></span>
              <span className="app-header-menu-line"></span>
            </button>
          )}
          {leftSlot}
        </div>

        <div className="app-header__center">
          {centerSlot || (
            <div className="app-header__title">
              {title &&
                (typeof title === 'string' ? (
                  <h2>{title}</h2>
                ) : (
                  <div className="app-header__title-node">{title}</div>
                ))}
              {subtitle && <span className="app-header__subtitle">{subtitle}</span>}
            </div>
          )}
        </div>

        {rightContent !== undefined && rightContent !== null && (
          <div className="app-header__right">
            {rightContent}
          </div>
        )}
      </div>

      {secondaryRow && <div className="app-header__secondary">{secondaryRow}</div>}
    </header>
  );
};

export default AppHeader;
